import { ActionReducerMapBuilder, AnyAction, createSlice } from '@reduxjs/toolkit';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { pageDataService } from 'common';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { debounce, omit } from 'lodash';
import { NotificationChannel, NotificationSettingPayload } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { createAsyncApiThunk, createOneOffAsyncApiThunk } from 'store/reduxHelpers';

import { INotificationsState } from './notifications.types';
import { getFamilyMemberNotificationSetting } from './notificationSelectors';

function getDefaultNotificationsState(): INotificationsState {
  return {
    notificationSettingsSummaryLoadStatus: ApiCallResultStatus.DEFAULT,
    notificationSettingsSummaryLoadError: undefined,
    notificationSettingsSummary: undefined,
    notificationSettingResubscribeStatus: undefined,
  };
}

const initialState: INotificationsState = getDefaultNotificationsState();

export const getNotificationSettingsSummaryAsync = createOneOffAsyncApiThunk(
  'notifications/getNotificationSettingsSummaryAsync',
  () => microsoftConsumerSecurityClient.notificationSettings.getNotificationSettings({ includeFamily: true }),
  ({ notifications }) =>
    notifications.notificationSettingsSummaryLoadStatus === ApiCallResultStatus.DEFAULT ||
    notifications.notificationSettingsSummaryLoadStatus === ApiCallResultStatus.FAILED
      ? ApiCallResultStatus.DEFAULT
      : notifications.notificationSettingsSummaryLoadStatus
);

const handleGetNotificationSettingsSummaryAsync = (builder: ActionReducerMapBuilder<INotificationsState>): void => {
  builder
    .addCase(getNotificationSettingsSummaryAsync.pending, (state) => {
      state.notificationSettingsSummaryLoadStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getNotificationSettingsSummaryAsync.fulfilled, (state, action) => {
      state.notificationSettingsSummaryLoadStatus = ApiCallResultStatus.SUCCESS;
      state.notificationSettingsSummary = action.payload;
    })
    .addCase(getNotificationSettingsSummaryAsync.rejected, (state, action) => {
      state.notificationSettingsSummaryLoadStatus = ApiCallResultStatus.FAILED;
      state.notificationSettingsSummaryLoadError = action.error.message;
    });
};

/**
 * Debounced version of {@link microsoftConsumerSecurityClient.notificationSettings.updateNotificationSettings} that
 * will be called after `debounceInterval`.
 * All pending updates are queued up until after the `debounceInterval` of no invocation.
 */
const debouncedUpdateNotificationSetting = (() => {
  /**
   * Debounce interval in milliseconds
   */
  const debounceInterval = 500;

  /**
   * Pending setting updates to be sent
   */
  const pendingUpdates: Record<string, NotificationSettingPayload> = {};

  /**
   * Create a key for identifying a setting
   *
   * @param payload
   */
  const getSettingKey = (payload: NotificationSettingPayload) => {
    const isEnabledKey: keyof NotificationSettingPayload = 'isEnabled';

    // Key is generated from all payload properties except `isEnabled`
    const key = JSON.stringify(omit(payload, isEnabledKey), Object.keys(payload).sort());

    return key;
  };

  /**
   * Queue up a setting update to be sent to the server
   *
   * @param update
   */
  const addUpdate = (update: NotificationSettingPayload) => {
    const key = getSettingKey(update);
    pendingUpdates[key] = update;
  };

  /**
   * Debounced update function
   */
  const debouncedUpdate = debounce(() => {
    // consume pending updates
    const payloads = Object.keys(pendingUpdates).map((key) => {
      const payload = pendingUpdates[key];
      delete pendingUpdates[key];
      return payload;
    });

    if (payloads.length === 0) {
      return undefined;
    }

    return microsoftConsumerSecurityClient.notificationSettings.updateNotificationSettings({ body: payloads });
  }, debounceInterval);

  return (update: NotificationSettingPayload) => {
    // add updates immediately
    addUpdate(update);
    // call debounced update
    return debouncedUpdate() ?? Promise.resolve(undefined);
  };
})();

export const updateSelfNotificationTopicSettingsAsync = createAsyncApiThunk(
  'notifications/updateSelfNotificationTopicSettingsAsync',
  (args: { channel: NotificationChannel; topicId: string; isEnabled: boolean }) =>
    debouncedUpdateNotificationSetting({
      ...args,
      settingType: 'SelfNotificationTopic',
    })
);

const handleUpdateSelfNotificationTopicSettingsAsync = (builder: ActionReducerMapBuilder<INotificationsState>): void => {
  builder.addCase(updateSelfNotificationTopicSettingsAsync.pending, (state, { meta }) => {
    // pro-actively set state. Show error on failure
    const { channel, topicId, isEnabled } = meta.arg;
    if (state.notificationSettingsSummary) {
      if (channel === 'Push') {
        const topicSettingIndex = state.notificationSettingsSummary.pushNotifications.findIndex((setting) =>
          localeIgnoreCaseEquals(setting.topicId, topicId)
        );
        if (topicSettingIndex >= 0) {
          state.notificationSettingsSummary.pushNotifications[topicSettingIndex].isEnabled = isEnabled;
        }
      } else {
        const topicSettingIndex = state.notificationSettingsSummary.emailNotifications.findIndex((setting) =>
          localeIgnoreCaseEquals(setting.topicId, topicId)
        );
        if (topicSettingIndex >= 0) {
          state.notificationSettingsSummary.emailNotifications[topicSettingIndex].isEnabled = isEnabled;
        }
      }
    }
  });
};

export const updateFamilyMemberNotificationTopicSettingsAsync = createAsyncApiThunk(
  'notifications/updateFamilyMemberNotificationTopicSettingsAsync',
  (args: { memberUserId: string; channel: NotificationChannel; topicId: string; isEnabled: boolean }) =>
    debouncedUpdateNotificationSetting({
      ...args,
      settingType: 'FamilyMemberNotificationTopic',
    })
);

const handleUpdateFamilyMemberNotificationTopicSettingsAsync = (builder: ActionReducerMapBuilder<INotificationsState>): void => {
  builder.addCase(updateFamilyMemberNotificationTopicSettingsAsync.pending, (state, { meta }) => {
    // pro-actively set state. Show error on failure
    const { memberUserId, channel, topicId, isEnabled } = meta.arg;
    if (state.notificationSettingsSummary?.familyMemberNotificationSettings) {
      const memberSettingIndex = state.notificationSettingsSummary.familyMemberNotificationSettings.findIndex((setting) =>
        localeIgnoreCaseEquals(setting.memberUserId, memberUserId)
      );

      if (memberSettingIndex >= 0) {
        if (channel === 'Push') {
          const topicIndex = state.notificationSettingsSummary.familyMemberNotificationSettings[
            memberSettingIndex
          ].pushNotifications.findIndex((setting) => localeIgnoreCaseEquals(setting.topicId, topicId));
          if (topicIndex >= 0) {
            state.notificationSettingsSummary.familyMemberNotificationSettings[memberSettingIndex].pushNotifications[
              topicIndex
            ].isEnabled = isEnabled;
          }
        } else {
          const topicIndex = state.notificationSettingsSummary.familyMemberNotificationSettings[
            memberSettingIndex
          ].emailNotifications.findIndex((setting) => localeIgnoreCaseEquals(setting.topicId, topicId));
          if (topicIndex >= 0) {
            state.notificationSettingsSummary.familyMemberNotificationSettings[memberSettingIndex].emailNotifications[
              topicIndex
            ].isEnabled = isEnabled;
          }
        }
      }
    }
  });
};

export const updateSelfNotificationSettingsAsync = createAsyncApiThunk(
  'notifications/updateSelfNotificationSettingsAsync',
  ({ channel, isEnabled }: { channel: NotificationChannel; isEnabled: boolean }, thunkApi) => {
    const { notifications } = thunkApi.getState();
    const channelTopicSettings =
      channel === 'Push'
        ? notifications.notificationSettingsSummary?.pushNotifications.filter(
            (setting) => !setting.disabledReason && setting.isEnabled !== isEnabled
          ) ?? []
        : notifications.notificationSettingsSummary?.emailNotifications.filter(
            (setting) => !setting.disabledReason && setting.isEnabled !== isEnabled
          ) ?? [];

    return Promise.all(
      // dispatch separate actions for updating the topic settings so the states are updated accordingly
      channelTopicSettings.map((topicSetting) =>
        thunkApi.dispatch(updateSelfNotificationTopicSettingsAsync({ ...topicSetting, isEnabled }) as unknown as AnyAction)
      )
    );
  }
);

export const updateFamilyMemberNotificationSettingsAsync = createAsyncApiThunk(
  'notifications/updateFamilyMemberNotificationSettingsAsync',
  ({ memberUserId, isEnabled }: { memberUserId: string; isEnabled: boolean }, thunkApi) => {
    const { notifications } = thunkApi.getState();
    const familyMemberNotificationSetting = getFamilyMemberNotificationSetting(notifications, memberUserId);
    const memberTopicSettings = [
      ...(familyMemberNotificationSetting?.pushNotifications ?? []),
      ...(familyMemberNotificationSetting?.emailNotifications ?? []),
    ].filter((setting) => !setting.disabledReason && setting.isEnabled !== isEnabled);

    return Promise.all(
      // dispatch separate actions for updating the topic settings so the states are updated accordingly
      memberTopicSettings.map((topicSetting) =>
        thunkApi.dispatch(updateFamilyMemberNotificationTopicSettingsAsync({ ...topicSetting, isEnabled }) as unknown as AnyAction)
      )
    );
  }
);

export const updateFamilyNotificationSettingsAsync = createAsyncApiThunk(
  'notifications/updateFamilyNotificationSettingsAsync',
  ({ isEnabled }: { isEnabled: boolean }, thunkApi) => {
    const { notifications } = thunkApi.getState();
    const memberUserIds =
      notifications.notificationSettingsSummary?.familyMemberNotificationSettings?.map(
        (memberSetting) => memberSetting.memberUserId
      ) || [];

    return Promise.all(
      // dispatch separate actions for updating each family member's setting states
      memberUserIds.map((memberUserId) =>
        thunkApi.dispatch(updateFamilyMemberNotificationSettingsAsync({ memberUserId, isEnabled }) as unknown as AnyAction)
      )
    );
  }
);

/**
 * Unauthenticated Notification Setting Updates
 */
export const unauthUpdateSelfEmailNotificationSettingAsync = createAsyncApiThunk(
  'notifications/unauthUpdateSelfEmailNotificationSettingAsync',
  async (isEnabled: boolean) => {
    const { token } = pageDataService.getUnsubscribeData() || {};
    const updatedSetting = await microsoftConsumerSecurityClient.unauthNotificationSettings.unauthUpdateSelfEmailNotificationSetting({
      token,
      body: { isEnabled },
    });
    return updatedSetting;
  }
);

const handleUnauthUpdateSelfEmailNotificationSettingAsync = (builder: ActionReducerMapBuilder<INotificationsState>): void => {
  builder
    .addCase(unauthUpdateSelfEmailNotificationSettingAsync.pending, (state) => {
      state.notificationSettingResubscribeStatus = {
        status: ApiCallResultStatus.LOADING,
        error: undefined,
        response: undefined,
      };
    })
    .addCase(unauthUpdateSelfEmailNotificationSettingAsync.fulfilled, (state, { payload }) => {
      state.notificationSettingResubscribeStatus = {
        status: ApiCallResultStatus.SUCCESS,
        response: payload,
      };
    })
    .addCase(unauthUpdateSelfEmailNotificationSettingAsync.rejected, (state, { error }) => {
      state.notificationSettingResubscribeStatus = {
        status: ApiCallResultStatus.FAILED,
        error: error.message,
      };
    });
};

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    handleGetNotificationSettingsSummaryAsync(builder);
    handleUpdateSelfNotificationTopicSettingsAsync(builder);
    handleUpdateFamilyMemberNotificationTopicSettingsAsync(builder);
    handleUnauthUpdateSelfEmailNotificationSettingAsync(builder);
  },
});

export default notificationsSlice.reducer;
